import { forwardRef, createContext, HTMLAttributes, ReactNode, useMemo } from 'react';
import { cn } from '../../lib/styles';
import { ChildPositionProvider } from '@page/core/hooks/dom/useChildPosition';

export interface TimelineProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  /**
   * Size of the bullets in pixels.
   * @default 16
   */
  bulletSize?: number;
  /**
   * Whether to show the bullet next to the title if there's only one item in the timeline.
   * Useful to show informative custom bullets.
   * @default false
   */
  showBulletInSingleItemTitle?: boolean;
}

type TimelineContextValue = Pick<TimelineProps, 'bulletSize' | 'showBulletInSingleItemTitle'>;

export const TimelineContext = createContext<TimelineContextValue | null>(null);

/**
 * Timeline component for displaying chronological events
 */
export const Timeline = forwardRef<HTMLDivElement, TimelineProps>(
  ({ className, children, bulletSize = 16, showBulletInSingleItemTitle = false, ...props }, ref) => {
    const contextValue = useMemo(
      () => ({ bulletSize, showBulletInSingleItemTitle }),
      [bulletSize, showBulletInSingleItemTitle],
    );
    return (
      <div ref={ref} className={cn('flex flex-col', className)} {...props}>
        <TimelineContext.Provider value={contextValue}>
          <ChildPositionProvider>{children}</ChildPositionProvider>
        </TimelineContext.Provider>
      </div>
    );
  },
);

Timeline.displayName = 'Timeline';
